<template>
  <div class="relative flex items-center justify-center w-full h-full" data-testid="viewer-image-backdrop" @click.self="openModal()">
    <img
      class="outline-none cursor-pointer max-w-full focus-visible:ring focus-visible:ring-blue-500/50"
      :style="{ maxHeight: maxImageHeight }"
      ref="imageRef"
      data-testid="viewer-content-image"
      @load="onImageLoad"
      @click="openModal()"
      @keyup.enter="openModal()"
      :src="props.src"
      :alt="alt"
      tabindex="0"
    />
    <div
      v-if="showExpandHint"
      class="absolute bottom-3 right-3 z-10 flex items-center gap-1.5 rounded-md bg-black/50 px-2.5 py-1.5 text-xs font-medium text-white pointer-events-none"
      data-testid="viewer-image-expand-hint"
    >
      <svg aria-hidden="true" width="16" height="16" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M7.5 1.5H10.5V4.5M4.5 10.5H1.5V7.5M10.5 1.5L7 5M1.5 10.5L5 7"
          stroke="white"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
      <span>Click to expand</span>
    </div>
    <TextSelectOverlay v-if="hasOcrData" :image-bounds="imageBounds" />
  </div>
</template>
<script setup lang="ts">
import { ref, computed, Ref, onMounted, onUnmounted, nextTick, watch } from 'vue';
import { usePhotoSwipe } from './../../composables/usePhotoSwipe';
import { useImageBounds } from './../../composables/useImageBounds';
import { TrackEvents, useTrack } from '@/composables/useTrack';
import { useViewerItemStore } from './../../store/useViewerItemStore';
import { useArtifactsStore } from '../../store/useArtifactsStore';
import TextSelectOverlay from '../ocr/TextSelectOverlay.vue';

const props = defineProps<{
  src: string;
  alt?: string;
  maxHeight?: string;
}>();

const viewerItemStore = useViewerItemStore();
const artifactsStore = useArtifactsStore();
const hasOcrData = computed(() => artifactsStore.isOcrDone && artifactsStore.hasOcrData);

const { init, open } = usePhotoSwipe(props.src);
const imageRef: Ref<HTMLImageElement | null> = ref(null);
const { track } = useTrack();
const { imageBounds, updateImageBounds } = useImageBounds(imageRef, hasOcrData);

const maxImageHeight = ref(props.maxHeight ?? '80vh');
const showExpandHint = ref(false);

const imageRenderedHeightBeforeMaxHeight = (img: HTMLImageElement) => {
  const containerWidth = img.parentElement?.getBoundingClientRect().width || img.getBoundingClientRect().width || img.naturalWidth;
  const widthScale = img.naturalWidth > 0 ? Math.min(1, containerWidth / img.naturalWidth) : 1;

  return img.naturalHeight * widthScale;
};

const updateExpandHint = () => {
  const img = imageRef.value;
  if (!img) {
    showExpandHint.value = false;
    return;
  }

  const maxHeight = Number.parseInt(maxImageHeight.value, 10);
  showExpandHint.value = Number.isFinite(maxHeight) && imageRenderedHeightBeforeMaxHeight(img) > maxHeight;
};

const updateMaxHeight = () => {
  const img = imageRef.value;
  if (!img) {
    return;
  }

  if (props.maxHeight) {
    maxImageHeight.value = props.maxHeight;
  } else {
    const available = img.parentElement?.getBoundingClientRect().height ?? 0;
    if (available > 100) {
      maxImageHeight.value = `${Math.floor(available)}px`;
    }
  }
  updateExpandHint();
};

let resizeRafId: number | undefined;
const onResize = () => {
  if (resizeRafId !== undefined) {
    cancelAnimationFrame(resizeRafId);
  }
  resizeRafId = requestAnimationFrame(updateMaxHeight);
};

watch(
  () => props.maxHeight,
  (maxHeight, previousMaxHeight) => {
    if (!maxHeight && previousMaxHeight) {
      maxImageHeight.value = '80vh';
      updateExpandHint();
      return;
    }

    updateMaxHeight();
  }
);

onMounted(async () => {
  await nextTick();
  updateMaxHeight();
  window.addEventListener('resize', onResize);
});

onUnmounted(() => {
  window.removeEventListener('resize', onResize);
  if (resizeRafId !== undefined) {
    cancelAnimationFrame(resizeRafId);
  }
});

// initialize lightbox after image is loaded - need width/height of the image
const onImageLoad = async () => {
  const width = imageRef.value ? imageRef.value.naturalWidth : undefined;
  const height = imageRef.value ? imageRef.value.naturalHeight : undefined;

  init(width, height);
  updateImageBounds();
  await nextTick();
  updateMaxHeight();
};

const openModal = () => {
  open();
  track(TrackEvents.VIEWER_ITEM_ZOOM_CLICK, { viewer: 'copernicus', hashid: viewerItemStore.item?.id });
};
</script>
<style lang="scss">
.pswp--custom-bg {
  --pswp-bg: rgb(56, 69, 90);
  --pswp-icon-color: var(--zinc-900, #18181b);
  --pswp-icon-color-secondary: #141c31;
  --pswp-icon-stroke-color: transparent;
}

.pswp__bg {
  background: rgb(56, 69, 90) !important;
}

.pswp {
  max-width: 100vw;
}

body .pswp__control-stack {
  position: absolute;
  top: calc(16px + env(safe-area-inset-top));
  right: calc(16px + env(safe-area-inset-right));
  z-index: 11 !important;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

body .pswp__zoom-controls {
  position: relative;
  display: flex;
  width: 44px;
  flex-direction: column;
  align-items: center;
}

body .pswp__zoom-controls::before {
  position: absolute;
  top: 4px;
  left: 2px;
  width: 40px;
  height: 80px;
  border: 1px solid var(--slate-200, #e1e8f0);
  border-radius: 999px;
  background: #fff;
  box-shadow: 0 4px 12px rgba(20, 28, 49, 0.18);
  content: '';
  pointer-events: none;
}

body .pswp__zoom-controls::after {
  position: absolute;
  top: 44px;
  left: 3px;
  z-index: 2;
  width: 38px;
  height: 1px;
  background: var(--slate-200, #e1e8f0);
  content: '';
  pointer-events: none;
}

body .pswp__button--close,
body .pswp__button--zoom-in,
body .pswp__button--zoom-out {
  position: relative !important;
  top: auto !important;
  right: auto !important;
  left: auto !important;
  z-index: 1 !important;
  width: 44px !important;
  height: 44px !important;
  margin: 0 !important;
  padding: 0 !important;
  opacity: 1;
  --pswp-icon-color: currentColor;

  color: var(--zinc-900, #18181b);
  background: transparent;
  transition: color 120ms ease;
}

body .pswp__button--close::before,
body .pswp__button--zoom-in::before,
body .pswp__button--zoom-out::before {
  position: absolute;
  left: 2px;
  z-index: -1;
  width: 40px;
  height: 40px;
  background-color: transparent;
  content: '';
  transition: background-color 120ms ease;
}

body .pswp__button--close::before {
  top: 2px;
  border: 1px solid var(--slate-200, #e1e8f0);
  border-radius: 999px;
  background-color: #fff;
  box-shadow: 0 4px 12px rgba(20, 28, 49, 0.18);
}

body .pswp__button--zoom-in::before {
  top: 4px;
  border-radius: 999px 999px 0 0;
}

body .pswp__button--zoom-out::before {
  top: 0;
  border-radius: 0 0 999px 999px;
}

body .pswp__button--close:hover::before,
body .pswp__button--zoom-in:hover:not(:disabled)::before,
body .pswp__button--zoom-out:hover:not(:disabled)::before {
  background-color: var(--slate-200, #e1e8f0);
}

body .pswp__button--close:active,
body .pswp__button--zoom-in:active:not(:disabled),
body .pswp__button--zoom-out:active:not(:disabled) {
  color: #fff;
}

body .pswp__button--close:active::before,
body .pswp__button--zoom-in:active:not(:disabled)::before,
body .pswp__button--zoom-out:active:not(:disabled)::before {
  background-color: var(--zinc-900, #18181b);
}

body .pswp__button--zoom-in:disabled,
body .pswp__button--zoom-out:disabled {
  cursor: default;
  opacity: 0.35;
}

body .pswp__button--close .pswp__icn,
body .pswp__button--zoom-in .pswp__icn,
body .pswp__button--zoom-out .pswp__icn {
  top: 50% !important;
  left: 50% !important;
  width: 20px;
  height: 20px;
  transform: translate(-50%, -50%);
}
</style>
